import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  Row,
  Stack,
  Table,
} from 'react-bootstrap';
import { toast } from 'react-toastify';
import Loader from '../components/Loader';
import NewsEditorForm from '../components/NewsEditorForm';
import { useFetchAthleteByUserIdQuery } from '../slices/athleteApiSlice';
import {
  useCreateNewsPostMutation,
  useCreateNewsImageUploadMutation,
  useGenerateReleaseDraftMutation,
  useGetAdminNewsPostByIdQuery,
  useGetAdminNewsPostsQuery,
  useModerateNewsCommentMutation,
  usePublishNewsPostMutation,
  useUpdateNewsPostMutation,
} from '../slices/newsApiSlice';
import { useSelector } from 'react-redux';

const emptyForm = {
  title: '',
  summary: '',
  body: '',
  imageUrl: '',
  imageAlt: '',
  tags: 'league event, announcement, community',
  status: 'draft',
  lockedComments: false,
};

const MAX_IMAGE_FILE_SIZE_BYTES = 5 * 1024 * 1024;
const ALLOWED_IMAGE_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp', 'image/gif']);

const AdminNewsScreen = () => {
  const { userInfo } = useSelector((state) => state.auth);
  const [selectedPostId, setSelectedPostId] = useState(null);
  const [formState, setFormState] = useState(emptyForm);
  const [selectedImageFile, setSelectedImageFile] = useState(null);
  const [releaseDraftState, setReleaseDraftState] = useState({
    title: '',
    range: '',
    notes: '',
  });
  const { data: athlete, isLoading: athleteLoading } = useFetchAthleteByUserIdQuery(userInfo?._id, {
    skip: !userInfo,
  });
  const { data: posts, isLoading, error } = useGetAdminNewsPostsQuery();
  const { data: selectedPostDetail } = useGetAdminNewsPostByIdQuery(selectedPostId, {
    skip: !selectedPostId,
  });
  const [createNewsPost, { isLoading: isCreating }] = useCreateNewsPostMutation();
  const [createNewsImageUpload, { isLoading: isUploadingImage }] = useCreateNewsImageUploadMutation();
  const [updateNewsPost, { isLoading: isUpdating }] = useUpdateNewsPostMutation();
  const [publishNewsPost, { isLoading: isPublishing }] = usePublishNewsPostMutation();
  const [generateReleaseDraft, { isLoading: isGeneratingDraft }] = useGenerateReleaseDraftMutation();
  const [moderateNewsComment, { isLoading: isModeratingComment }] = useModerateNewsCommentMutation();

  const selectedPost = useMemo(() => {
    if (selectedPostDetail?._id === selectedPostId) {
      return selectedPostDetail;
    }

    return posts?.find((post) => post._id === selectedPostId) || null;
  }, [posts, selectedPostDetail, selectedPostId]);

  useEffect(() => {
    if (!selectedPost && posts?.length && !selectedPostId) {
      setSelectedPostId(posts[0]._id);
    }
  }, [posts, selectedPost, selectedPostId]);

  useEffect(() => {
    if (!selectedPost) {
      setFormState(emptyForm);
      setSelectedImageFile(null);
      return;
    }

    setFormState({
      title: selectedPost.title || '',
      summary: selectedPost.summary || '',
      body: selectedPost.body || '',
      imageUrl: selectedPost.imageUrl || '',
      imageAlt: selectedPost.imageAlt || '',
      tags: selectedPost.tags?.join(', ') || '',
      status: selectedPost.status || 'draft',
      lockedComments: Boolean(selectedPost.lockedComments),
    });
    setSelectedImageFile(null);
  }, [selectedPost]);

  const handleFormChange = (field, value) => {
    setFormState((current) => ({ ...current, [field]: value }));
  };

  const handleImageFileSelected = (file) => {
    setSelectedImageFile(file || null);
  };

  const handleImageUpload = async () => {
    if (!selectedImageFile) {
      toast.error('Choose an image to upload.');
      return;
    }

    if (!ALLOWED_IMAGE_TYPES.has(selectedImageFile.type)) {
      toast.error('Only JPEG, PNG, WEBP, and GIF images are supported.');
      return;
    }

    if (selectedImageFile.size > MAX_IMAGE_FILE_SIZE_BYTES) {
      toast.error('Image size must be 5 MB or smaller.');
      return;
    }

    try {
      const uploadTarget = await createNewsImageUpload({
        fileName: selectedImageFile.name,
        contentType: selectedImageFile.type,
        fileSize: selectedImageFile.size,
      }).unwrap();

      const uploadResponse = await fetch(uploadTarget.uploadUrl, {
        method: uploadTarget.method || 'PUT',
        headers: uploadTarget.headers || { 'Content-Type': selectedImageFile.type },
        body: selectedImageFile,
      });

      if (!uploadResponse.ok) {
        throw new Error('Upload request failed.');
      }

      setFormState((current) => ({
        ...current,
        imageUrl: uploadTarget.publicUrl,
        imageAlt: current.imageAlt || current.title,
      }));
      setSelectedImageFile(null);
      toast.success('Image uploaded.');
    } catch (error) {
      toast.error(error?.data?.message || error.message || 'Failed to upload image');
    }
  };

  const handleSave = async (event) => {
    event.preventDefault();

    try {
      if (selectedPost) {
        await updateNewsPost({ id: selectedPost._id, ...formState }).unwrap();
        toast.success('News post updated.');
      } else {
        const created = await createNewsPost(formState).unwrap();
        setSelectedPostId(created._id);
        toast.success('Draft created.');
      }
    } catch (saveError) {
      toast.error(saveError?.data?.message || 'Failed to save news post');
    }
  };

  const handlePublish = async () => {
    if (!selectedPost) {
      toast.error('Choose a post first.');
      return;
    }

    try {
      const result = await publishNewsPost({ id: selectedPost._id, sendToDiscord: true }).unwrap();
      if (result.discord?.error) {
        toast.warning(`Published on site, but Discord had an issue: ${result.discord.error}`);
      } else {
        toast.success('Published to League Runner and Discord.');
      }
    } catch (publishError) {
      toast.error(publishError?.data?.message || 'Failed to publish update');
    }
  };

  const handleGenerateDraft = async (event) => {
    event.preventDefault();

    try {
      const created = await generateReleaseDraft(releaseDraftState).unwrap();
      setSelectedPostId(created._id);
      toast.success('Technical draft generated.');
    } catch (draftError) {
      toast.error(draftError?.data?.message || 'Failed to generate technical draft');
    }
  };

  const handleCommentModeration = async (commentId, status) => {
    try {
      await moderateNewsComment({ commentId, status }).unwrap();
      toast.success(`Comment marked ${status}.`);
    } catch (commentError) {
      toast.error(commentError?.data?.message || 'Failed to update comment');
    }
  };

  if (athleteLoading || isLoading) {
    return <Loader />;
  }

  if (!athlete?.isAdmin) {
    return (
      <div className="section-modern">
        <Container className="py-5">
          <Alert variant="danger">Not authorized as admin.</Alert>
        </Container>
      </div>
    );
  }

  return (
    <div className="section-modern">
      <Container className="fade-in py-5">
        <div className="d-flex justify-content-between align-items-start flex-wrap gap-3 mb-4">
          <div>
            <h1 className="text-primary mb-2">Stories & Announcements</h1>
            <p className="text-secondary mb-0">
              Publish league-event recaps, season announcements, community spotlights, and other marketing stories to the site and Discord.
            </p>
          </div>
          <Button
            variant="outline-primary"
            onClick={() => {
              setSelectedPostId(null);
              setFormState(emptyForm);
            }}
          >
            New Story
          </Button>
        </div>

        {error ? (
          <Alert variant="danger">{error?.data?.message || 'Failed to load admin news data.'}</Alert>
        ) : (
          <Row className="g-4">
            <Col xl={4}>
              <Stack gap={4}>
                <Card className="modern-card">
                  <Card.Body>
                    <h2 className="h5 mb-2">Technical Draft Generator</h2>
                    <p className="text-secondary small mb-3">
                      Optional: use this only when you want to turn a git range into a technical update draft.
                    </p>
                    <Form onSubmit={handleGenerateDraft}>
                      <Form.Group className="mb-3">
                        <Form.Label>Optional technical title</Form.Label>
                        <Form.Control
                          type="text"
                          value={releaseDraftState.title}
                          onChange={(event) =>
                            setReleaseDraftState((current) => ({ ...current, title: event.target.value }))
                          }
                        />
                      </Form.Group>
                      <Form.Group className="mb-3">
                        <Form.Label>Git range</Form.Label>
                        <Form.Control
                          type="text"
                          value={releaseDraftState.range}
                          onChange={(event) =>
                            setReleaseDraftState((current) => ({ ...current, range: event.target.value }))
                          }
                          placeholder="HEAD~5..HEAD"
                        />
                      </Form.Group>
                      <Form.Group className="mb-3">
                        <Form.Label>Prompt notes</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={4}
                          value={releaseDraftState.notes}
                          onChange={(event) =>
                            setReleaseDraftState((current) => ({ ...current, notes: event.target.value }))
                          }
                          placeholder="Describe the technical changes you want summarized."
                        />
                      </Form.Group>
                      <Button type="submit" disabled={isGeneratingDraft}>
                        {isGeneratingDraft ? 'Generating...' : 'Generate Technical Draft'}
                      </Button>
                    </Form>
                  </Card.Body>
                </Card>

                <Card className="modern-card">
                  <Card.Body>
                    <h2 className="h5 mb-3">Recent Stories & Drafts</h2>
                    {posts?.length ? (
                      <Stack gap={2}>
                        {posts.map((post) => (
                          <Button
                            key={post._id}
                            variant={selectedPostId === post._id ? 'primary' : 'outline-secondary'}
                            className="text-start"
                            onClick={() => setSelectedPostId(post._id)}
                          >
                            <div className="fw-semibold">{post.title}</div>
                            <div className="small opacity-75">
                              {post.status} · {new Date(post.updatedAt).toLocaleDateString()}
                            </div>
                          </Button>
                        ))}
                      </Stack>
                    ) : (
                      <div className="text-secondary">No stories yet.</div>
                    )}
                  </Card.Body>
                </Card>
              </Stack>
            </Col>

            <Col xl={8}>
              <Card className="modern-card">
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-start flex-wrap gap-3 mb-4">
                    <div>
                      <h2 className="h4 mb-1">{selectedPost ? 'Edit Story' : 'Create Story'}</h2>
                      <div className="text-secondary small">
                        {selectedPost ? `Slug: ${selectedPost.slug}` : 'Drafts stay private until published.'}
                      </div>
                    </div>
                    {selectedPost ? (
                      <div className="d-flex gap-2 align-items-center flex-wrap">
                        <Badge bg={selectedPost.status === 'published' ? 'success' : 'secondary'}>
                          {selectedPost.status}
                        </Badge>
                        <Button onClick={handlePublish} disabled={isPublishing}>
                          {isPublishing ? 'Publishing...' : 'Publish Story + Discord'}
                        </Button>
                      </div>
                    ) : null}
                  </div>

                  <NewsEditorForm
                    formState={formState}
                    onChange={handleFormChange}
                    onSubmit={handleSave}
                    isSaving={isCreating || isUpdating}
                    onImageFileSelected={handleImageFileSelected}
                    onUploadImage={handleImageUpload}
                    isUploadingImage={isUploadingImage}
                    selectedImageName={selectedImageFile?.name || ''}
                    submitLabel={selectedPost ? 'Save Story Changes' : 'Create Story Draft'}
                  />

                  {selectedPost?.discordPublishError ? (
                    <Alert variant="warning" className="mt-4 mb-0">
                      Discord publish issue: {selectedPost.discordPublishError}
                    </Alert>
                  ) : null}
                </Card.Body>
              </Card>

              {selectedPost ? (
                <Card className="modern-card mt-4">
                  <Card.Body>
                    <h2 className="h5 mb-3">Comment Moderation</h2>
                    {selectedPost.comments?.length ? (
                      <Table responsive hover>
                        <thead>
                          <tr>
                            <th>User</th>
                            <th>Comment</th>
                            <th>Status</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedPost.comments.map((comment) => (
                            <tr key={comment._id}>
                              <td>{comment.user?.username || 'User'}</td>
                              <td style={{ whiteSpace: 'pre-wrap' }}>{comment.body}</td>
                              <td>{comment.status}</td>
                              <td>
                                <div className="d-flex gap-2">
                                  <Button
                                    size="sm"
                                    variant="outline-warning"
                                    disabled={isModeratingComment}
                                    onClick={() =>
                                      handleCommentModeration(
                                        comment._id,
                                        comment.status === 'hidden' ? 'visible' : 'hidden'
                                      )
                                    }
                                  >
                                    {comment.status === 'hidden' ? 'Show' : 'Hide'}
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    ) : (
                      <div className="text-secondary">No comments on this post yet.</div>
                    )}
                  </Card.Body>
                </Card>
              ) : null}
            </Col>
          </Row>
        )}
      </Container>
    </div>
  );
};

export default AdminNewsScreen;
